import { Injectable } from '@angular/core';
import {
  LENGTH_STATUS_LONG,
  LENGTH_STATUS_MEDIUM,
  LENGTH_STATUS_SHORT,
  LENGTH_STATUS_THRESHOLDS,
  LENGTH_MIN_SECONDS,
  LENGTH_MAX_SECONDS,
} from '../constants/edit-asset-modal.constants';
import { Asset, LengthState, PrimaryAssetFields, SelectionState, SubAsset, VariantState } from '../models/asset.model';

@Injectable({
  providedIn: 'root',
})
export class EditAssetModalHelperService {
  private readonly secondsPatternWithQuote: RegExp = /^\d{1,2}"$/;
  private readonly secondsPatternWithPlusQuote: RegExp = /^\d{1,2}\+\s*"$/;
  private readonly secondsPatternWithoutQuote: RegExp = /^\d{1,2}$/;

  public formatSeconds(seconds: number): string {
    if (seconds >= LENGTH_MAX_SECONDS) {
      return `${LENGTH_MAX_SECONDS}+"`;
    }

    return `${String(seconds).padStart(2, '0')}"`;
  }

  public convertToDateObject(timestamp: unknown): { year: number; month: number; day: number } | '' {
    if (!timestamp) {
      return '';
    }

    if (
      typeof timestamp === 'object' &&
      timestamp !== null &&
      'year' in timestamp &&
      'month' in timestamp &&
      'day' in timestamp
    ) {
      const { year, month, day } = timestamp as { year: number; month: number; day: number };
      return { year, month, day };
    }

    const date: Date = new Date(Number(timestamp));
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  public convertToTimestamp(dateObj: { year: number; month: number; day: number } | null | undefined): string | null {
    if (!dateObj) {
      return null;
    }

    const date: Date = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
    return date.getTime().toString();
  }

  public initializeLengthState(selectedLengths: string[] | undefined | null): LengthState {
    const selectedLength: string | undefined = selectedLengths?.[0];
    if (!selectedLength) {
      return this.buildLengthStateFromSeconds(LENGTH_MIN_SECONDS);
    }

    const [maybeStatus, maybeValue] = selectedLength.split(' ');
    const valueToken: string = maybeValue ?? maybeStatus ?? '';
    const parsedSeconds: number | null = this.parseLengthInput(valueToken);

    if (parsedSeconds === null) {
      return this.buildLengthStateFromSeconds(LENGTH_MIN_SECONDS);
    }

    const clampedSeconds: number = this.clampLengthSeconds(parsedSeconds);
    const state: LengthState = this.buildLengthStateFromSeconds(clampedSeconds);

    if (maybeValue) {
      state.lengthStatus = maybeStatus;
    }

    return state;
  }

  public ensureSelectedFormat(
    selectedFormat: string | null | undefined,
    selectedFormats: string[] | undefined | null,
    availableFormat: string[] | undefined | null
  ): string {
    if (selectedFormat) {
      return selectedFormat;
    }

    if (selectedFormats?.length) {
      return selectedFormats[0];
    }

    return availableFormat?.[0] || '';
  }

  public parseLengthInput(value: string): number | null {
    if (!value) {
      return null;
    }

    if (this.secondsPatternWithPlusQuote.test(value)) {
      return Number.parseInt(value.split('+').join('').split('"').join(''), 10);
    }

    if (this.secondsPatternWithQuote.test(value)) {
      return Number.parseInt(value.split('"').join(''), 10);
    }

    if (this.secondsPatternWithoutQuote.test(value)) {
      return Number.parseInt(value, 10);
    }

    return null;
  }

  public isLengthInRange(seconds: number): boolean {
    return seconds >= LENGTH_MIN_SECONDS && seconds <= LENGTH_MAX_SECONDS;
  }

  public clampLengthSeconds(seconds: number): number {
    return Math.min(Math.max(seconds, LENGTH_MIN_SECONDS), LENGTH_MAX_SECONDS);
  }

  public buildLengthStateFromSeconds(seconds: number): LengthState {
    const clampedSeconds: number = this.clampLengthSeconds(seconds);

    return {
      lengthStatus: this.determineLengthStatus(clampedSeconds),
      lengthValue: this.formatSeconds(clampedSeconds),
      lengthValueInSeconds: clampedSeconds,
    };
  }

  public deepClone<T>(value: T): T {
    if (value === null || value === undefined) {
      return value;
    }

    const globalStructuredClone: ((val: T) => T) | undefined = (globalThis as { structuredClone?: <U>(val: U) => U })
      .structuredClone;
    if (typeof globalStructuredClone === 'function') {
      return globalStructuredClone(value);
    }

    return JSON.parse(JSON.stringify(value));
  }

  public extractPrimaryFields(asset: Asset): PrimaryAssetFields {
    return {
      region: asset.region ?? '',
      agency: asset.agency ?? '',
      product: asset.product ?? '',
      type: asset.type,
      focus: asset.focus ?? '',
      detail: asset.detail,
    };
  }

  public buildSelectionState(
    asset: Asset,
    betList: string[],
    availableFormat: string[],
    currentSelectedFormat: string
  ): SelectionState {
    const bets: string[] = this.deepClone(asset.bets || []);
    const selectedFormats: string[] = this.deepClone(asset.format || []);
    const selectedLengths: string[] = this.deepClone(asset.length || []);
    const selectedCategories: string[] = this.deepClone(asset.categories || []);
    const otherBet: string = bets.find((bet: string) => !betList.includes(bet)) || '';
    const selectedBets: string[] = bets.filter((bet: string) => betList.includes(bet));
    const maybeReleaseDate: { year: number; month: number; day: number } | '' | null = asset.releaseDate
      ? this.convertToDateObject(this.deepClone(asset.releaseDate))
      : null;
    const releaseDate: { year: number; month: number; day: number } | null | undefined =
      !maybeReleaseDate || typeof maybeReleaseDate === 'string' ? null : maybeReleaseDate;
    const lengthState: LengthState = this.initializeLengthState(selectedLengths);

    return {
      selectedBets,
      otherBet,
      selectedFormats,
      selectedLengths,
      selectedCategories,
      releaseDate,
      lengthState,
      selectedFormat: this.ensureSelectedFormat(currentSelectedFormat, selectedFormats, availableFormat),
    };
  }

  public buildVariantState(
    asset: Asset,
    status: string,
    affectedAssets: Record<string, { subAsset: SubAsset[]; status?: string }> | undefined
  ): VariantState {
    const assetId: string | undefined = asset.id;
    const tempAssets: SubAsset[] = this.deepClone(asset.subAsset || []);

    if (!assetId) {
      return { tempAssets };
    }

    const existingAffected: { subAsset: SubAsset[]; status?: string } | undefined = affectedAssets?.[assetId];
    const statusFromAffected: string | undefined = existingAffected?.status;
    if (!existingAffected) {
      return { tempAssets, statusFromAffected };
    }

    const affectedEntry: { subAsset: SubAsset[]; status?: string } = this.deepClone({
      ...existingAffected,
      subAsset: existingAffected.subAsset ?? [],
    });

    const subAssetList: SubAsset[] = affectedEntry.subAsset ?? [];

    for (const variant of tempAssets) {
      const variantId: string | undefined = variant?.id;
      if (!variantId) {
        continue;
      }

      const alreadyTracked: boolean = subAssetList.some((entry: SubAsset) => entry.id === variantId);
      if (!alreadyTracked) {
        subAssetList.push({ id: variantId, status: variant.status || status });
      }
    }

    affectedEntry.subAsset = subAssetList;

    return {
      tempAssets,
      affectedEntry,
      statusFromAffected,
    };
  }

  private determineLengthStatus(seconds: number): string {
    if (seconds <= LENGTH_STATUS_THRESHOLDS.SHORT_MAX) {
      return LENGTH_STATUS_SHORT;
    }

    if (seconds <= LENGTH_STATUS_THRESHOLDS.MEDIUM_MAX) {
      return LENGTH_STATUS_MEDIUM;
    }

    return LENGTH_STATUS_LONG;
  }
}
